/**
 * Per-admin alert inbox (top-header bell + My Notifications sidebar).
 * Each logged-in admin has their own localStorage bucket so accounts
 * on the same browser do not share read/clear state.
 */
const LEGACY_STORAGE_KEY = "naseebi_admin_alerts_v1";
const MAX_ITEMS = 60;
const listeners = new Set();

/** @type {number|string|null} */
let activeAdminId = null;
/** @type {Array<object>} */
let items = [];
/** Header-bell tray hide watermark (does not delete stored alerts). */
let headerClearedAt = 0;

function storageKeyFor(adminId) {
  const id = adminId != null && adminId !== "" ? String(adminId) : "anon";
  return `naseebi_admin_alerts_v2_${id}`;
}

function headerClearedKeyFor(adminId) {
  const id = adminId != null && adminId !== "" ? String(adminId) : "anon";
  return `naseebi_admin_header_cleared_v1_${id}`;
}

function loadHeaderClearedAt(adminId) {
  try {
    const n = Number(localStorage.getItem(headerClearedKeyFor(adminId)));
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function saveHeaderClearedAt(adminId, ts) {
  try {
    localStorage.setItem(headerClearedKeyFor(adminId), String(ts));
  } catch {
    // ignore quota
  }
}

function loadFor(adminId) {
  try {
    const key = storageKeyFor(adminId);
    let raw = localStorage.getItem(key);
    // One-time migrate from the old shared inbox into this admin's bucket.
    if (!raw && adminId != null) {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        raw = legacy;
      }
    }
    const parsed = raw ? JSON.parse(raw) : [];
    return sortAlertsNewestFirst(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
}

function save() {
  try {
    items = sortAlertsNewestFirst(items).slice(0, MAX_ITEMS);
    localStorage.setItem(storageKeyFor(activeAdminId), JSON.stringify(items));
  } catch {
    // ignore quota
  }
}

function alertTime(alert) {
  const t = new Date(alert?.receivedAt || alert?.at || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

export function sortAlertsNewestFirst(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) => alertTime(b) - alertTime(a));
}

function emit() {
  listeners.forEach((fn) => {
    try {
      fn(items);
    } catch (_) {}
  });
}

/**
 * Switch (or initialize) the in-memory inbox for the given admin.
 * Call whenever the logged-in admin id is known / changes.
 */
export function setAdminAlertsOwner(adminId) {
  const nextId =
    adminId != null && Number.isFinite(Number(adminId)) ? Number(adminId) : adminId || null;
  if (String(activeAdminId ?? "") === String(nextId ?? "")) {
    return items;
  }
  activeAdminId = nextId;
  items = loadFor(activeAdminId);
  headerClearedAt = loadHeaderClearedAt(activeAdminId);
  emit();
  return items;
}

export function getAdminAlertsOwner() {
  return activeAdminId;
}

export function getAdminAlerts(adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    return loadFor(adminId);
  }
  if (activeAdminId == null && adminId != null) {
    setAdminAlertsOwner(adminId);
  }
  return items;
}

export function getUnreadAdminAlertCount(adminId) {
  return getAdminAlerts(adminId).filter((a) => !a.read).length;
}

export function getHeaderInboxClearedAt(adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    return loadHeaderClearedAt(adminId);
  }
  if (activeAdminId == null && adminId != null) {
    setAdminAlertsOwner(adminId);
  }
  return headerClearedAt;
}

function isVisibleInHeaderInbox(alert, clearedAt) {
  return alertTime(alert) > Number(clearedAt || 0);
}

/** Alerts shown in the top-header bell. Independent of My Notifications. */
export function getHeaderInboxAlerts(adminId) {
  const clearedAt = getHeaderInboxClearedAt(adminId);
  return getAdminAlerts(adminId).filter((a) => isVisibleInHeaderInbox(a, clearedAt));
}

export function getHeaderInboxUnreadCount(adminId) {
  return getHeaderInboxAlerts(adminId).filter((a) => !a.read).length;
}

/**
 * Hide every current alert from the header bell only.
 * Does not delete or mark-read items; My Notifications stays intact.
 */
export function clearHeaderInbox(adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  headerClearedAt = Date.now();
  saveHeaderClearedAt(activeAdminId, headerClearedAt);
  emit();
}

export function subscribeAdminAlerts(fn) {
  listeners.add(fn);
  fn(items);
  return () => listeners.delete(fn);
}

export function pushAdminAlert(alert, adminId) {
  if (!alert?.id) return;
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  const exists = items.some((a) => a.id === alert.id);
  if (exists) return;
  items = sortAlertsNewestFirst([
    { ...alert, read: false, receivedAt: alert.at || new Date().toISOString() },
    ...items,
  ]).slice(0, MAX_ITEMS);
  save();
  emit();
}

export function markAdminAlertUnread(id, adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  items = items.map((a) => (a.id === id ? { ...a, read: false } : a));
  save();
  emit();
}

export function markAdminAlertRead(id, adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  items = items.map((a) => (a.id === id ? { ...a, read: true } : a));
  save();
  emit();
}

export function markAllAdminAlertsRead(adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  items = items.map((a) => ({ ...a, read: true }));
  save();
  emit();
}

export function clearAdminAlerts(adminId) {
  if (adminId != null && String(adminId) !== String(activeAdminId ?? "")) {
    setAdminAlertsOwner(adminId);
  }
  items = [];
  save();
  emit();
}

/** Shared search for topbar dropdown + My Notifications page. */
export function alertMatchesSearch(alert, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const meta = alert?.meta || {};
  const haystack = [
    alert.title,
    alert.message,
    alert.type,
    alert.kind,
    alert.route,
    alert.refId,
    meta.fromName,
    meta.displayName,
    meta.name,
    meta.subjectName,
    meta.agentName,
    meta.fromUserId,
    meta.userId,
    meta.ticketId,
    meta.chatId,
    meta.email,
    meta.phone,
    meta.preview,
    meta.channel,
  ]
    .filter((v) => v != null && v !== "")
    .map((v) => String(v).toLowerCase())
    .join(" ");
  return haystack.includes(q);
}

export function getAlertChannel(alert) {
  const channel = String(alert?.meta?.channel || alert?.kind || "").toLowerCase();
  if (channel === "sales" || channel === "support" || channel === "csr") return channel;
  if (alert?.type === "chat_message" || alert?.type === "user_online") {
    if (String(alert?.route || "").includes("sales-crm")) return "sales";
    if (String(alert?.route || "").includes("support")) return "support";
    return "csr";
  }
  return "";
}

export function alertAccent(type) {
  switch (type) {
    case "support_ticket":
      return {
        bg: "bg-rose-50",
        border: "border-rose-200",
        badge: "bg-rose-100 text-rose-700",
        icon: "🎫",
        label: "Support",
      };
    case "user_registered":
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        badge: "bg-emerald-100 text-emerald-700",
        icon: "👋",
        label: "New user",
      };
    case "verification_pending":
      return {
        bg: "bg-amber-50",
        border: "border-amber-200",
        badge: "bg-amber-100 text-amber-800",
        icon: "✅",
        label: "Verification",
      };
    case "chat_message":
      return {
        bg: "bg-sky-50",
        border: "border-sky-200",
        badge: "bg-sky-100 text-sky-800",
        icon: "💬",
        label: "Chat",
      };
    case "user_online":
      return {
        bg: "bg-green-50",
        border: "border-green-200",
        badge: "bg-green-100 text-green-800",
        icon: "🟢",
        label: "Online",
      };
    default:
      return {
        bg: "bg-slate-50",
        border: "border-slate-200",
        badge: "bg-slate-100 text-slate-700",
        icon: "🔔",
        label: "Alert",
      };
  }
}
